import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { LogOut, Mail, Shield, X } from 'lucide-react';

const GoogleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

const buttonStyle = {
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  color: 'var(--text-primary)',
};

const infoCardStyle = {
  background: 'color-mix(in srgb, var(--surface) 82%, var(--bg) 18%)',
  border: '1px solid color-mix(in srgb, var(--border) 72%, transparent)',
};

export const AuthButton = ({ user, onSignIn, onSignOut }) => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open]);

  if (!user) {
    return (
      <button
        onClick={onSignIn}
        className="flex items-center gap-2 px-3 py-2 text-[13px] font-semibold rounded-[14px] transition-all hover:scale-105 active:scale-95 shadow-sm"
        style={{ color: 'var(--text-primary)', background: 'var(--bg)', border: '2px solid var(--border-s)' }}
        title="Accedi con Google"
      >
        <GoogleIcon />
        Accedi
      </button>
    );
  }

  const avatarUrl = user.user_metadata?.avatar_url;
  const displayName = user.user_metadata?.full_name || user.email;
  const initial = (displayName || '?')[0].toUpperCase();
  const accountCreatedAt = user.created_at
    ? new Date(user.created_at).toLocaleDateString('it-IT', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    })
    : null;

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="w-9 h-9 rounded-full overflow-hidden shadow-[0_2px_8px_rgba(122,184,187,0.3)] hover:scale-105 active:scale-95 transition-all"
        style={{ border: '2px solid color-mix(in srgb, var(--accent-2) 40%, transparent)' }}
        title={displayName}
      >
        {avatarUrl ? (
          <img src={avatarUrl} alt={displayName} className="w-full h-full object-cover" referrerPolicy="no-referrer" />
        ) : (
          <div className="w-full h-full flex items-center justify-center font-bold text-[14px]" style={{ background: 'linear-gradient(135deg, var(--accent-2), var(--accent))', color: 'var(--bg-deeper)' }}>
            {initial}
          </div>
        )}
      </button>

      {open && createPortal(
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4 backdrop-blur-sm"
          style={{ background: 'color-mix(in srgb, var(--bg-deeper) 64%, transparent)' }}
          onClick={() => setOpen(false)}
        >
          <div
            className="w-full max-w-md rounded-[24px] shadow-2xl overflow-hidden"
            style={{ background: 'var(--bg-dim)', border: '1px solid var(--border)' }}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between px-5 py-4" style={{ borderBottom: '1px solid var(--border)' }}>
              <div>
                <p className="text-[18px] font-bold" style={{ color: 'var(--text-primary)' }}>Account Google</p>
                <p className="text-[13px] mt-1" style={{ color: 'var(--text-muted)' }}>Dettagli profilo e sessione attiva</p>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="w-9 h-9 rounded-[14px] flex items-center justify-center transition-all hover:scale-105"
                style={buttonStyle}
                title="Chiudi"
              >
                <X size={18} />
              </button>
            </div>

            <div className="px-5 py-5 space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full overflow-hidden shrink-0" style={{ border: '2px solid color-mix(in srgb, var(--accent-2) 40%, transparent)' }}>
                  {avatarUrl ? (
                    <img src={avatarUrl} alt={displayName} className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center font-bold text-[22px]" style={{ background: 'linear-gradient(135deg, var(--accent-2), var(--accent))', color: 'var(--bg-deeper)' }}>
                      {initial}
                    </div>
                  )}
                </div>
                <div className="min-w-0">
                  <p className="text-[17px] font-semibold truncate" style={{ color: 'var(--text-primary)' }}>{displayName}</p>
                  <p className="text-[13px] truncate mt-1" style={{ color: 'var(--text-muted)' }}>{user.email}</p>
                </div>
              </div>

              <div className="space-y-3">
                <div className="rounded-[18px] px-4 py-3 flex items-start gap-3" style={infoCardStyle}>
                  <Mail size={16} className="mt-0.5 shrink-0" style={{ color: 'var(--accent-2)' }} />
                  <div className="min-w-0">
                    <p className="text-[12px] font-semibold uppercase tracking-wide" style={{ color: 'var(--accent-2)' }}>Email</p>
                    <p className="text-[14px] mt-1 break-all" style={{ color: 'var(--text-primary)' }}>{user.email}</p>
                  </div>
                </div>

                <div className="rounded-[18px] px-4 py-3 flex items-start gap-3" style={infoCardStyle}>
                  <Shield size={16} className="mt-0.5 shrink-0" style={{ color: 'var(--accent)' }} />
                  <div className="min-w-0">
                    <p className="text-[12px] font-semibold uppercase tracking-wide" style={{ color: 'var(--accent)' }}>Provider</p>
                    <p className="text-[14px] mt-1" style={{ color: 'var(--text-primary)' }}>Google</p>
                    {accountCreatedAt && (
                      <p className="text-[12px] mt-1" style={{ color: 'var(--text-dim)' }}>Accesso creato il {accountCreatedAt}</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex justify-end pt-2">
                <button
                  onClick={() => {
                    setOpen(false);
                    onSignOut();
                  }}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-[14px] text-[13px] font-semibold transition-all hover:scale-[1.02]"
                  style={{
                    background: 'color-mix(in srgb, var(--danger) 18%, var(--surface) 82%)',
                    border: '1px solid color-mix(in srgb, var(--danger) 34%, var(--border) 66%)',
                    color: 'color-mix(in srgb, var(--danger) 74%, var(--text-primary) 26%)',
                  }}
                >
                  <LogOut size={15} />
                  Logout
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};
